// src/layouts/AspirantLayout.tsx - PRODUCTION READY VERSION
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  MessageSquare, 
  BarChart, 
  Map, 
  Settings, 
  LogOut,
  Plus,
  Bell,
  Target,
  Globe,
  ChevronDown,
  ChevronRight,
  BookOpen,
} from 'lucide-react';
import { Header } from '../components/Header';
import { CreateCampaignModal } from '../components/CreateCampaignModal';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// Extended Aspirant type for layout display
interface ExtendedAspirant {
  id: number;
  name: string;
  phoneNumber: string;
  email?: string;
  position: string;
  tier?: string;
  constituency?: string;
  ward?: string;
  party?: string;
  languagePreference?: string;
  receiveSmsAlerts?: boolean;
  receiveEmailAlerts?: boolean;
  apiKey?: string;
  createdAt?: string;
  lastLogin?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  constituencyName?: string;
  wardName?: string;
  wardId?: number;
}

const AspirantLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, aspirant: authAspirant, refreshAspirantDetails } = useAuth();
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Auto-refresh aspirant data if ward info is missing
  useEffect(() => {
    const refreshData = async () => {
      // Check if we have aspirant data but missing ward info
      if (authAspirant && (!authAspirant.wardName || !authAspirant.wardId)) {
        console.log('🔄 Aspirant data missing ward info, attempting to refresh...');
        setIsRefreshing(true);
        
        try {
          if (refreshAspirantDetails) {
            await refreshAspirantDetails();
            // Silent success - no toast in production to avoid annoying users
          } else {
            console.warn('refreshAspirantDetails function not available');
          }
        } catch (error) {
          console.error('Failed to refresh aspirant details:', error);
          // Silent fail in production - don't show error to users
        } finally {
          setIsRefreshing(false);
        }
      }
    };

    refreshData();
  }, [authAspirant, refreshAspirantDetails]);

  // Convert authAspirant to ExtendedAspirant for display
  const aspirant: ExtendedAspirant | null = authAspirant ? {
    id: authAspirant.id,
    name: authAspirant.name,
    phoneNumber: authAspirant.phoneNumber,
    email: authAspirant.email,
    position: authAspirant.position,
    tier: authAspirant.tier,
    constituency: typeof authAspirant.constituency === 'string' ? authAspirant.constituency : undefined,
    party: authAspirant.party,
    languagePreference: authAspirant.languagePreference,
    receiveSmsAlerts: authAspirant.receiveSmsAlerts,
    receiveEmailAlerts: authAspirant.receiveEmailAlerts,
    createdAt: (authAspirant as any).createdAt,
    subscriptionStartDate: (authAspirant as any).subscriptionStartDate,
    subscriptionEndDate: (authAspirant as any).subscriptionEndDate,
    // These will be populated if they exist in the auth context
    wardName: (authAspirant as any).wardName,
    wardId: (authAspirant as any).wardId,
    constituencyName: (authAspirant as any).constituencyName,
  } : null;

  const campaignSubItems = [
    { path: '/aspirant/campaigns', label: 'All Campaigns' },
    //{ path: '/aspirant/campaigns/scheduled', label: 'Scheduled' },
    //{ path: '/aspirant/campaigns/drafts', label: 'Drafts' },
    //{ path: '/aspirant/campaigns/sent', label: 'Sent Campaigns' },
    //{ path: '/aspirant/campaigns/analytics', label: 'Analytics' },
  ];

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // Get aspirant info for display - SAFELY
  const getAspirantInitials = () => {
    if (aspirant?.name) {
      const names = aspirant.name.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return aspirant.name.substring(0, 2).toUpperCase();
    }
    return 'A';
  };

  const getAspirantWard = () => {
    // Try multiple possible property names
    if (aspirant?.wardName) return aspirant.wardName;
    if (aspirant?.ward) return aspirant.ward;
    if ((aspirant as any)?.ward) return (aspirant as any).ward;
    
    // Default fallback
    return 'Your Ward';
  };

  const getAspirantLocation = () => {
    // Try multiple possible property names
    if (aspirant?.constituencyName) return aspirant.constituencyName;
    if (aspirant?.constituency) return aspirant.constituency;
    
    // Default fallback
    return 'Constituency';
  };

  const getAspirantWardId = () => {
    if (aspirant?.wardId) return aspirant.wardId;
    if ((aspirant as any)?.wardId) return (aspirant as any).wardId;
    
    // Default fallback
    return 'N/A';
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex flex-col flex-1 pt-5 bg-white border-r border-gray-200">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 px-4 mb-8">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold">CC</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">Pamoja Connect</h1>
                <p className="text-xs text-gray-500">Aspirant Portal</p>
              </div>
            </div>
          </div>

          {/* Quick Create Buttons */}
          <div className="px-4 mb-6">
            <button
              onClick={() => setShowCreateCampaign(true)}
              className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg font-medium hover:from-primary-700 hover:to-blue-700 transition-all shadow-md"
            >
              <Plus size={18} className="mr-2" />
              New Campaign
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {/* Dashboard */}
            <button
              onClick={() => navigate('/aspirant/dashboard')}
              className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive('/aspirant/dashboard', true)
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Home size={18} className="mr-3" />
              Dashboard
            </button>

            {/* Campaigns with Dropdown */}
            <div className="space-y-1">
              <button
                onClick={() => setCampaignMenuOpen(!campaignMenuOpen)}
                className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive('/aspirant/campaigns')
                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center">
                  <MessageSquare size={18} className="mr-3" />
                  Campaigns
                </div>
                {campaignMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              
              {campaignMenuOpen && (
                <div className="ml-8 space-y-1">
                  {campaignSubItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center px-3 py-2 text-sm rounded-lg transition-colors ${
                        location.pathname === item.path
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="mr-2">•</span>
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Memorandums */}
            <button
              onClick={() => navigate('/aspirant/memorandums')}
              className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive('/aspirant/memorandums')
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FileText size={18} className="mr-3" />
              Memorandums
            </button>

            {/* Reports */}
            <button
              onClick={() => navigate('/aspirant/reports')}
              className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive('/aspirant/reports')
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BarChart size={18} className="mr-3" />
              Reports
            </button>

            {/* Heat Map */}
            <button
              onClick={() => navigate('/aspirant/heatmap')}
              className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive('/aspirant/heatmap')
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Map size={18} className="mr-3" />
              Heat Map
            </button>

            {/* Manifesto */}
            <button
              onClick={() => navigate('/aspirant/manifesto')}
              className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive('/aspirant/manifesto')
                  ? 'bg-green-50 text-green-700 border border-green-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <BookOpen size={18} className="mr-3" />
              Manifesto
              <span className="ml-auto px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                New
              </span>
            </button>

            {/* Settings */}
            <button
              onClick={() => navigate('/aspirant/settings')}
              className={`w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors ${
                isActive('/aspirant/settings')
                  ? 'bg-primary-50 text-primary-700 border border-primary-200'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings size={18} className="mr-3" />
              Settings
            </button>
          </nav>

          {/* Bottom Section */}
          <div className="flex-shrink-0 p-4 border-t border-gray-200">
            {/* Notifications */}
            <button className="flex items-center justify-between w-full px-3 py-2 mb-3 text-sm text-gray-700 rounded-lg hover:bg-gray-100">
              <div className="flex items-center">
                <Bell size={16} className="mr-2" />
                Notifications
              </div>
              <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                3
              </span>
            </button>

            {/* Profile Info */}
            <div className="px-3 py-3 mb-3 bg-gray-50 rounded-lg">
              <div className="flex items-center mb-2">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-primary-600 font-bold">
                    {getAspirantInitials()}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {aspirant?.name || 'Aspirant Name'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Ward: {getAspirantWard()}
                  </p>
                </div>
              </div>
              <div className="flex items-center text-xs text-gray-600">
                <Target size={12} className="mr-1" />
                <span className="mr-3">Ward ID: {getAspirantWardId()}</span>
                <Globe size={12} className="mr-1" />
                <span>{getAspirantLocation()}</span>
              </div>
            </div>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center px-3 py-3 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors"
            >
              <LogOut size={18} className="mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:pl-64 flex flex-col flex-1">
        {/* Mobile Header */}
        <div className="md:hidden bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white font-bold">CC</span>
              </div>
              <h1 className="text-lg font-bold text-gray-900">
                {location.pathname.includes('manifesto') ? 'Manifesto' : 
                 location.pathname.includes('campaigns') ? 'Campaigns' : 
                 location.pathname.includes('memorandums') ? 'Memorandums' : 
                 'Aspirant Portal'}
              </h1>
            </div>
            <div className="flex space-x-2">
              {location.pathname.includes('manifesto') && (
                <button
                  onClick={() => navigate('/aspirant/manifesto')}
                  className="flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium"
                >
                  <BookOpen size={16} className="mr-1" />
                  Manifesto
                </button>
              )}
              <button
                onClick={() => setShowCreateCampaign(true)}
                className="flex items-center px-3 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium"
              >
                <Plus size={16} className="mr-1" />
                New
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateCampaign && (
        <CreateCampaignModal 
          onClose={() => setShowCreateCampaign(false)}
          onSuccess={() => {
            window.dispatchEvent(new Event('campaign-created'));
            navigate('/aspirant/campaigns');
          }}
        />
      )}
    </div>
  );
};

export default AspirantLayout;